import { fabric } from 'fabric'
import { v4 as guid } from 'uuid'
import { isEmpty, pick } from 'lodash-es'
import type MyArticle from '@/models/myArticle'
import appConfig from '@/services/appConfig'
import utils from '@/services/utils'
import { useUserStore } from '@/store/userData'
import { merchConstants } from '@/models/constants'

interface IMbModelDetailsOptions extends fabric.ITextboxOptions {
  id?: string
  articleId?: number
  modelId?: number
  article?: MyArticle
  showLabels?: boolean
  attributes?: string[]
  attributesSeparator?: string
  attributePlaceHolder?: string
  locked?: boolean
  templateObject?: boolean
  showModelAttributesAndCurrentSeasonSKUCount?: boolean
}

const editableProps: Record<string, IMbObjectProp> = {
  fill: { name: 'text color', type: 'textColor' },
  backgroundColor: { name: 'backgroundColor color', type: 'color' },
  font: { name: 'font', type: 'font' },
  fontSize: { name: 'font size', type: 'fontSize' },
  textAlign: { name: 'align', type: 'textAlign' },
  fontStyle: { name: 'font style', type: 'fontStyle' },
  attributes: { name: 'attributes', type: 'attributes' },
  locked: { name: 'Lock', type: 'lock' },
  showLabels: { name: 'show labels', type: 'showLabels' },
}

// eslint-disable-next-line ts/ban-ts-comment
// @ts-expect-error
export default class MbModelDetails extends fabric.Textbox implements IMbObject {
  public id: string
  public modelId?: number
  public articleId?: number
  public article?: MyArticle
  public showLabels: boolean
  public attributes?: string[]
  public attributesSeparator?: string
  public attributePlaceHolder?: string
  public type = merchConstants.objectTypes.modelDetails
  public locked: boolean
  public templateObject: boolean
  public showModelAttributesAndCurrentSeasonSKUCount: boolean

  public editableProps: Record<string, IMbObjectProp> = editableProps

  public actions: Record<string, IWObjectActions> = {
    selectSimilar: { action: 'selectSimilar', label: 'Select Similar', faicon: 'fa-light fa-check-double', showInSubMenu: true },
    bringFront: { action: 'bringFront', label: 'Bring to Front', faicon: 'fa-light fa-bring-front', showInSubMenu: true },
    sendBack: { action: 'sendBack', label: 'Send to Back', faicon: 'fa-light fa-send-back', showInSubMenu: true },
    group: { action: 'group', label: 'Group', faicon: 'fa-light fa-object-group', showInSubMenu: true, multiple: true },
    copy: { action: 'copy', label: 'Copy Selection', faicon: 'fa-light fa-copy', showInSubMenu: true },
    align: { action: 'align', label: 'Align', faicon: 'fa-light fa-align-justify', showInSubMenu: true },
    delete: { action: 'delete', label: 'Remove', faicon: 'fa-light fa-trash-can', showInSubMenu: true },
  }

  public static defaultAttributes = ['ModelNumber', 'ModelName']

  constructor(text: string, thumbnailSize: string, options: IMbModelDetailsOptions) {
    super(text, options)
    this.id = options?.id || guid()
    this.modelId = options?.modelId
    this.articleId = options?.articleId
    this.article = options?.article
    this.showLabels = utils.isDefined(options?.showLabels) ? options.showLabels : false
    this.attributes = options?.attributes?.length ? options.attributes : [...MbModelDetails.defaultAttributes]
    this.attributesSeparator = options?.attributesSeparator || '\n'
    this.attributePlaceHolder = options?.attributePlaceHolder || '[Blank]'
    const userStore = useUserStore()
    this.fontFamily = options?.fontFamily || userStore.settingsContainer?.merch?.modelDetailsFont || appConfig.dynamicConfig?.merchDefaultFont || 'Times New Roman'
    this.fontSize = options?.fontSize || (utils.isValidStringValue(userStore.settingsContainer?.merch?.articleDetailsFontSize) ? userStore.settingsContainer.merch.articleDetailsFontSize * 96 / 72 : merchConstants.slideArticleThumbnailFontSizes[thumbnailSize] ? merchConstants.slideArticleThumbnailFontSizes[thumbnailSize] : merchConstants.slideArticleThumbnailFontSizes.large)
    this.textAlign = options?.textAlign || 'center'
    this.fill = options?.fill || userStore.settingsContainer?.merch?.modelDetailsFontColor || '#000000'
    this.locked = options?.locked || false
    this.templateObject = options?.templateObject || false
    this.showModelAttributesAndCurrentSeasonSKUCount = options?.showModelAttributesAndCurrentSeasonSKUCount || false

    this.editable = false

    // const userStore = useUserStore()
    // const catalogDetails = this.catalogCode ? (userStore.linkedCatalogDetails[this.catalogCode] || userStore.activeCatalog) : userStore.activeCatalog!
    // // if previous season model article need to add the current season article as well in the live query to support the updates in sku count of current catalog.
    // const queryCriterion: Array<[number, number, string]> = [[userStore.activeCatalog!.CatalogCode, 1, this.modelNumber || '']]
    // if (catalogDetails.CatalogCode !== userStore.activeCatalog?.CatalogCode) {
    //   queryCriterion.push([+catalogDetails!.CatalogCode, 1, this.modelNumber || ''])
    // }
    // const observable = liveQuery(async () => await appConfig.DB!.articles.where('[CatalogCode+Status+ModelNumber]').anyOf(queryCriterion).toArray())
    // const subscription = observable.subscribe(async (response) => {
    //   // As discussed with Andre we dont need to set article again when we first time create the object as already we are loading the article
    //   if (!this.isLoadingFirstTime) {
    //     const article = response.find(article => article.CatalogCode === catalogDetails.CatalogCode)
    //     if (article) {
    //       const articles = await appConfig.DB!.buildMyArticles([article], catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, true)
    //       this.article = articles[0]
    //       this.set('text', await MbModelDetails.getText(this.article, this.showLabels, this.attributes, this.attributesSeparator, this.attributePlaceHolder))
    //       this.dirty = true
    //       this.canvas?.requestRenderAll()
    //       this.canvas?.fire('object:modified', { target: this, ignoreHistory: true })
    //     }
    //   }
    //   else {
    //     this.isLoadingFirstTime = false
    //   }
    // })
    // this.subscriptions.push(subscription)

    this.set('text', text)
    this.objectCaching = false
    this.setLock(this.locked)

    this.on('selected', () => {
      this.selected = true
    })

    this.on('deselected', () => {
      this.selected = false
    })

    this.stateProperties = this.stateProperties?.concat(['showLabels', 'locked', 'attributes'])
  }

  async setProp(prop: string, value: any, ignoreHistory: boolean = false) {
    switch (prop) {
      case 'top':
        this.set('top', value.top)
        break
      case 'left':
        this.set('left', value.left)
        break
      case 'fill':
        this.set('fill', value.fill)
        break
      case 'backgroundColor':
        this.set('backgroundColor', value.backgroundColor)
        break
      case 'font':
        this.set('fontFamily', value.font)
        break
      case 'fontSize':
        this.set('fontSize', value.fontSize)
        break
      case 'textAlign':
        this.set('textAlign', value.textAlign)
        break
      case 'bold':
        this.set('fontWeight', value.bold ? 'bold' : '')
        break
      case 'italic':
        this.set('fontStyle', value.italic ? 'italic' : '')
        break
      case 'underline':
        this.set('underline', value.underline)
        break
      case 'fontStyle':
        this.set('fontStyle', value.fontStyle)
        break
      case 'locked':
        this.set('locked', value.locked)
        this.setLock(value.locked)
        break
      case 'showLabels':
        this.set('showLabels', value.showLabels)
        this.set('text', await this.getAttributesValue(this.attributes, value.showLabels))
        break
      case 'attributes': {
        this.set('attributes', value.attributes)
        this.set('text', await this.getAttributesValue(value.attributes, this.showLabels))
        break
      }
      case 'text':
        // value in case of set text value should be fetch from article
        this.set('text', await this.getAttributesValue(this.attributes, this.showLabels))
        break
      case 'height':
        this.set('height', value)
        break
      default:
        console.warn('Attempting to set unsupported MbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    this.canvas?.fire('object:modified', { target: this, ignoreHistory })
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'fill':
        result.textColor = this.fill
        break
      case 'backgroundColor':
        result.backgroundColor = this.backgroundColor
        break
      case 'font':
        result.font = this.fontFamily
        break
      case 'fontSize':
        result.fontSize = this.fontSize
        break
      case 'textAlign':
        result.textAlign = this.textAlign
        break
      case 'bold':
        result.bold = !!this.fontWeight
        break
      case 'italic':
        result.italic = !!this.fontStyle
        break
      case 'underline':
        result.underline = !!this.underline
        break
      case 'fontStyle':
        result.fontStyle = this.fontStyle
        break
      case 'locked':
        result.lock = this.locked
        break
      case 'showLabels':
        result.showLabels = this.showLabels
        break
      case 'attributes':
        result.attributes = this.attributes
        break
      case 'text':
        result.text = this.text
        break
      default:
        console.warn('Attempting to get unsupported MbObjectProp', prop)
    }
    return result
  }

  setLock(lock: boolean) {
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'type',
      'top',
      'left',
      'angle',
      'width',
      'height',
      'scaleX',
      'scaleY',
      'fill',
      'backgroundColor',
      'flipX',
      'flipY',
      'fontFamily',
      'fontSize',
      'fontStyle',
      'underline',
      'fontWeight',
      'textAlign',
      'locked',
      'showLabels',
      'templateObject',
      'modelId',
      'articleId',
      'attributes',
      'attributesSeparator',
      'attributePlaceHolder',
      'showModelAttributesAndCurrentSeasonSKUCount',
    ]
    const returnObject = pick(this, propsToPluck)
    return returnObject
  }

  static fromObject(object: MbModelDetails, callback?: Function) {
    if (object.modelId) {
      this.loadModelDetailsByModelId(object.modelId, object)
        .then((v) => { callback && callback(v) })
    }
  }

  // this function will always resolve to a value
  static async getText(article: MyArticle, showLabels: boolean, attributes?: string[], attributesSeparator?: string, attributePlaceHolder?: string) {
    const userStore = useUserStore()
    const attributeData: any[] = []
    if (attributes && attributes.length) {
      for (let i = 0; i < attributes.length; i++) {
        const attributeSystemName = attributes[i]
        const attributeObject = userStore.myAttributes?.[attributeSystemName]
        let attributeValue
        if (utils.isDefined(attributeObject) && attributeObject.IsModelLevel) {
          attributeValue = await utils.getAttributeTypeSpecificValue(attributeObject, article, appConfig.DB, userStore.activeCatalog, userStore.priceGroups)
          if (!utils.isValidStringValue(attributeValue)) {
            attributeValue = attributePlaceHolder
          }
          let label = ''
          if (showLabels && userStore.activeCatalog) {
            const merchLabelAttributesConfigValue = userStore.activeCatalog.Config.MerchLabelAttributes
            if (merchLabelAttributesConfigValue === undefined
              || (utils.isDefined(merchLabelAttributesConfigValue) && Array.isArray(merchLabelAttributesConfigValue) && merchLabelAttributesConfigValue!.includes(attributeObject.SystemName))) {
              label = attributeObject.DisplayName
            }
          }
          attributeData.push(showLabels && label !== '' ? `${label}: ${attributeValue}` : attributeValue)
        }
        else if (attributeSystemName === 'currentSKuCount' && userStore.activeCatalog) {
          try {
            attributeValue = article.hasOwnProperty(attributeSystemName) ? article[attributeSystemName] : await appConfig.DB!.getModelSKUCount(article.ModelNumber, userStore.activeCatalog.CatalogCode)
          }
          catch (error) {
            console.error('Unable get model SKU count \n', error)
            attributeValue = attributePlaceHolder
          }
          article[attributeSystemName] = attributeValue
          attributeData.push(`${userStore.activeCatalog.Season} SKU: ${attributeValue}`)
        }
      }
    }
    return attributeData.join(attributesSeparator)
  }

  static async loadModelDetails(article: MyArticle, thumbnailSize: string, options?: IMbModelDetailsOptions) {
    const currentOptions: IMbModelDetailsOptions = options || { modelId: article.ModelId, articleId: article.Id }
    currentOptions.modelId = article.ModelId
    currentOptions.articleId = article.Id
    currentOptions.showLabels = utils.isDefined(options?.showLabels) ? options.showLabels : false
    currentOptions.attributes = options?.attributes
    const detailsText = await this.getText(article, currentOptions.showLabels, currentOptions.attributes, currentOptions.attributesSeparator || '\n', currentOptions.attributePlaceHolder)
    const modelDetailsObject = new MbModelDetails(detailsText, thumbnailSize, currentOptions)
    modelDetailsObject.article = article
    return modelDetailsObject
  }

  static async loadModelDetailsByModelId(modelId: number, options?: IMbModelDetailsOptions, imageSize?: number) {
    const thumbnailSize = imageSize === merchConstants.slideImageDefaultScaleFactors.medium
      ? 'medium'
      : imageSize === merchConstants.slideImageDefaultScaleFactors.small
        ? 'small'
        : 'large'
    if (options?.article) {
      return await this.loadModelDetails(options.article, thumbnailSize, options)
    }
    else if (modelId) {
      const userStore = useUserStore()
      if (appConfig.DB && userStore.activeCatalog && userStore.myAttributes) {
        const articles = await appConfig.DB.getFirstActiveMyArticleByModelIds([modelId], userStore.activeCatalog, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, [], true)
        if (articles && articles.length) {
          return await this.loadModelDetails(articles[0], thumbnailSize, options)
        }
      }
    }
    console.warn('Failed to load model details by modelId')
    return null
  }

  private async getAttributesValue(attributes: string[] | undefined, showLabels: boolean, attributesSeparator?: string, attributePlaceHolder?: string) {
    return this.article ? await MbModelDetails.getText(this.article, showLabels, attributes, attributesSeparator, attributePlaceHolder) : ''
  }

  static getEditableProps() {
    return editableProps
  }
}

const f: any = fabric
f.MbModelDetails = MbModelDetails
